import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';

interface ActivityItem {
  id: string;
  type: 'session' | 'match' | 'user' | 'venue';
  description: string;
  timestamp: string;
  exactTimestamp?: string;
  venueName?: string;
  playerName?: string;
  createdBy?: string;
  createdByName?: string;
  result?: 'win' | 'loss' | 'draw';
  score?: string;
}

interface LeaderboardEntry {
  userId: string;
  name: string;
  score: number;
  rank: number;
}

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, isAdmin, isVenueOperator, userRole, venueIds: currentUserVenueIds } = useAuth();
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<ActivityItem | null>(null);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showAllActivitiesModal, setShowAllActivitiesModal] = useState(false);
  const [allActivities, setAllActivities] = useState<ActivityItem[]>([]);
  const [topPlayers, setTopPlayers] = useState<LeaderboardEntry[]>([]);

  useEffect(() => {
    loadRecentActivity();
    loadTopPlayers();
  }, [i18n.language]);

  const loadTopPlayers = async () => {
    try {
      // Load enabled leaderboards
      const leaderboardsQuery = query(
        collection(db, 'leaderboards'),
        orderBy('createdAt', 'desc'),
        limit(1)
      );
      const leaderboardsSnapshot = await getDocs(leaderboardsQuery);

      if (leaderboardsSnapshot.empty) {
        // No leaderboards created yet, use mock data
        setTopPlayers([
          { userId: '1', name: 'Nagy Péter', score: 2847, rank: 1 },
          { userId: '2', name: 'Kiss Anna', score: 2654, rank: 2 },
          { userId: '3', name: 'Szabó János', score: 2431, rank: 3 }
        ]);
        return;
      }

      // For now, generate top players based on user stats
      // In production, this would calculate from actual game data
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const players: LeaderboardEntry[] = [];

      usersSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.displayName && data.displayName !== 'Játékos' && data.displayName !== 'User') {
          // Calculate score based on available data (mock for now)
          const score = Math.floor(Math.random() * 3000) + 1000;
          players.push({
            userId: doc.id,
            name: data.displayName,
            score,
            rank: 0
          });
        }
      });

      // Sort by score and assign ranks
      players.sort((a, b) => b.score - a.score);
      players.forEach((player, index) => {
        player.rank = index + 1;
      });

      setTopPlayers(players.slice(0, 3));
    } catch (error) {
      console.error('Error loading top players:', error);
      // Fallback to mock data
      setTopPlayers([
        { userId: '1', name: 'Nagy Péter', score: 2847, rank: 1 },
        { userId: '2', name: 'Kiss Anna', score: 2654, rank: 2 },
        { userId: '3', name: 'Szabó János', score: 2431, rank: 3 }
      ]);
    }
  };

  const loadRecentActivity = async () => {
    try {
      const activities: ActivityItem[] = [];
      const allTimestamps: { id: string; timestamp: Date }[] = [];

      // Load recent sessions
      const sessionsQuery = query(
        collection(db, 'sessions'),
        orderBy('startTime', 'desc'),
        limit(10)
      );
      const sessionsSnapshot = await getDocs(sessionsQuery);

      sessionsSnapshot.forEach((doc) => {
        const data = doc.data();

        // Filter for venue operators - only show their venues
        if (isVenueOperator && !isAdmin && currentUserVenueIds.length > 0) {
          if (!currentUserVenueIds.includes(data.venueId)) {
            return;
          }
        }

        const timestamp = new Date(data.startTime);
        allTimestamps.push({ id: doc.id, timestamp });

        // Determine result text
        let resultText = '';
        if (data.result === 'win') {
          resultText = ` - ${t('common.won')}`;
        } else if (data.result === 'loss') {
          resultText = ` - ${t('common.lost')}`;
        } else if (data.result === 'draw') {
          resultText = ` - ${t('common.draw')}`;
        }

        activities.push({
          id: doc.id,
          type: 'session',
          description: data.status === 'active'
            ? `${t('dashboard.sessionStarted')}: ${data.venueName || t('common.venue')}`
            : `${t('dashboard.sessionCompleted')}: ${data.venueName || t('common.venue')} (${data.matchCount || 0} ${t('common.matches')})${resultText}`,
          timestamp: '',
          exactTimestamp: timestamp.toLocaleString('hu-HU', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }),
          venueName: data.venueName,
          playerName: data.playerName,
          createdBy: data.createdBy,
          createdByName: data.playerName,
          result: data.result,
          score: data.score
        });
      });

      // Load recent venues
      const venuesQuery = query(
        collection(db, 'venues'),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      const venuesSnapshot = await getDocs(venuesQuery);

      venuesSnapshot.forEach((doc) => {
        const data = doc.data();

        // Filter for venue operators
        if (isVenueOperator && !isAdmin && currentUserVenueIds.length > 0) {
          if (!currentUserVenueIds.includes(doc.id)) {
            return;
          }
        }

        const timestamp = new Date(data.createdAt);
        allTimestamps.push({ id: `venue-${doc.id}`, timestamp });

        activities.push({
          id: `venue-${doc.id}`,
          type: 'venue',
          description: `${t('dashboard.venueAdded')}: ${data.name}`,
          timestamp: '',
          exactTimestamp: timestamp.toLocaleString('hu-HU', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }),
          venueName: data.name,
          createdBy: data.createdBy,
          createdByName: data.operatorName
        });
      });

      // Load recent users
      const usersQuery = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      const usersSnapshot = await getDocs(usersQuery);

      usersSnapshot.forEach((doc) => {
        const data = doc.data();
        const timestamp = new Date(data.createdAt);
        allTimestamps.push({ id: `user-${doc.id}`, timestamp });

        let currentRole = '';
        let previousRole = t('common.user'); // Default previous role

        if (data.isAdmin) {
          currentRole = t('common.admin');
          // If they have venue operator flag, they were promoted from venue operator
          if (data.previousRole === 'venueOperator') {
            previousRole = t('common.venueOperator');
          }
        } else if (data.isVenueOperator) {
          currentRole = t('common.venueOperator');
        } else {
          currentRole = t('common.user');
          // If they're now a regular user, they might have been demoted
          if (data.previousRole === 'admin') {
            previousRole = t('common.admin');
          } else if (data.previousRole === 'venueOperator') {
            previousRole = t('common.venueOperator');
          }
        }

        activities.push({
          id: `user-${doc.id}`,
          type: 'user',
          description: `${t('dashboard.userRoleChanged')}: ${previousRole} → ${currentRole}`,
          timestamp: '',
          exactTimestamp: timestamp.toLocaleString('hu-HU', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }),
          createdBy: data.modifiedBy || data.createdBy,
          createdByName: data.modifiedByName || user?.displayName || 'Admin'
        });
      });

      // Calculate time ago for all activities and sort
      const now = new Date();
      activities.forEach(activity => {
        const timestampData = allTimestamps.find(t => t.id === activity.id);
        if (timestampData) {
          const diffMs = now.getTime() - timestampData.timestamp.getTime();
          const diffMins = Math.floor(diffMs / 60000);
          const diffHours = Math.floor(diffMins / 60);
          const diffDays = Math.floor(diffHours / 24);

          if (diffDays > 0) {
            activity.timestamp = `${diffDays} ${t('common.daysAgo')}`;
          } else if (diffHours > 0) {
            activity.timestamp = `${diffHours} ${t('common.hoursAgo')}`;
          } else if (diffMins > 0) {
            activity.timestamp = `${diffMins} ${t('common.minutesAgo')}`;
          } else {
            activity.timestamp = t('common.justNow');
          }
        }
      });

      // Sort by most recent
      activities.sort((a, b) => {
        const aTime = allTimestamps.find(t => t.id === a.id)?.timestamp.getTime() || 0;
        const bTime = allTimestamps.find(t => t.id === b.id)?.timestamp.getTime() || 0;
        return bTime - aTime;
      });

      setRecentActivity(activities.slice(0, 5));
      setAllActivities(activities); // Store all activities for the full list modal
    } catch (error) {
      console.error('Error loading recent activity:', error);
    }
  };

  return (
    <>
      <div className="mb-4">
        <h1 className="text-3xl font-bold text-gradient-cyan mb-1">
          {t('dashboard.welcomeBack', { name: user?.displayName?.split(' ')[0] })}
        </h1>
        <p className="text-[#B8B8C3] text-sm">
          {isAdmin ? t('dashboard.adminAccess') : isVenueOperator ? t('dashboard.venueOperatorAccess') : t('dashboard.todayActivity')}
        </p>
      </div>

      {/* Statistics Cards - Only for Admins and Venue Operators */}
      {(isAdmin || isVenueOperator) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          <div className="stat-card text-center">
            <div className="flex items-center justify-center mb-2">
              <div className="p-2 bg-[#00AFF2]/10 rounded-lg">
                <svg className="w-6 h-6 text-[#00AFF2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
            <p className="stat-label">{t('dashboard.totalUsers')}</p>
            <p className="stat-number">1,234</p>
            <p className="text-xs text-[#2bc48a] mt-1">↗ +{t('dashboard.fromLastMonth', { percent: 12 })}</p>
          </div>

          <div className="stat-card text-center">
            <div className="flex items-center justify-center mb-2">
              <div className="p-2 bg-[#d9a514]/10 rounded-lg trophy-icon">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="stat-label">{t('dashboard.revenue')}</p>
            <p className="stat-number">$45,231</p>
            <p className="text-xs text-[#2bc48a] mt-1">↗ +{t('dashboard.fromLastMonth', { percent: 8 })}</p>
          </div>

          <div className="stat-card text-center">
            <div className="flex items-center justify-center mb-2">
              <div className="p-2 bg-[#2bc48a]/10 rounded-lg">
                <svg className="w-6 h-6 text-[#2bc48a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <p className="stat-label">{t('dashboard.activeSessions')}</p>
            <p className="stat-number">573</p>
            <p className="text-xs text-[#00AFF2] mt-1">⚡ {t('dashboard.fromLastHour', { percent: 3 })}</p>
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="card-dark p-4 mb-4">
          <h2 className="text-xl font-bold text-gradient-cyan mb-3">{t('dashboard.adminPanel')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <Link
              to="/users"
              className="p-3 sm:p-5 card-dark-hover text-left group"
            >
              <h3 className="font-semibold text-white mb-1 group-hover:text-[#00AFF2] transition-colors text-sm">{t('dashboard.userManagement')}</h3>
              <p className="text-xs text-[#8a8f99] line-clamp-1">{t('dashboard.userManagementDesc')}</p>
            </Link>
            <Link
              to="/venues"
              className="p-3 sm:p-5 card-dark-hover text-left group"
            >
              <h3 className="font-semibold text-white mb-1 group-hover:text-[#00AFF2] transition-colors text-sm">{t('dashboard.venueManagement')}</h3>
              <p className="text-xs text-[#8a8f99] line-clamp-1">{t('dashboard.venueManagementDesc')}</p>
            </Link>
            <Link
              to="/statistics"
              className="p-3 sm:p-5 card-dark-hover text-left group"
            >
              <h3 className="font-semibold text-white mb-1 group-hover:text-[#00AFF2] transition-colors text-sm">{t('dashboard.venueStatistics')}</h3>
              <p className="text-xs text-[#8a8f99] line-clamp-1">{t('dashboard.venueStatisticsDesc')}</p>
            </Link>
            <Link
              to="/reports"
              className="p-3 sm:p-5 card-dark-hover text-left group"
            >
              <h3 className="font-semibold text-white mb-1 group-hover:text-[#00AFF2] transition-colors text-sm">{t('dashboard.revenueReports')}</h3>
              <p className="text-xs text-[#8a8f99] line-clamp-1">{t('dashboard.revenueReportsDesc')}</p>
            </Link>
            <Link
              to="/sessions"
              className="p-3 sm:p-5 card-dark-hover text-left group"
            >
              <h3 className="font-semibold text-white mb-1 group-hover:text-[#00AFF2] transition-colors text-sm">{t('dashboard.sessionManagement')}</h3>
              <p className="text-xs text-[#8a8f99] line-clamp-1">{t('dashboard.sessionManagementDesc')}</p>
            </Link>
            <button className="p-3 card-dark-hover text-left group">
              <h3 className="font-semibold text-white mb-1 group-hover:text-[#00AFF2] transition-colors text-sm">{t('dashboard.systemSettings')}</h3>
              <p className="text-xs text-[#8a8f99] line-clamp-1">{t('dashboard.systemSettingsDesc')}</p>
            </button>
          </div>
        </div>
      )}

      {isVenueOperator && !isAdmin && (
        <div className="card-dark p-3 mb-4">
          <h2 className="text-xl font-bold text-gradient-cyan mb-3">{t('dashboard.venueOperatorPanel')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Link
              to="/statistics"
              className="p-3 card-dark-hover text-left group"
            >
              <h3 className="font-semibold text-white mb-1 group-hover:text-[#00AFF2] transition-colors text-sm">{t('dashboard.venueStatistics')}</h3>
              <p className="text-xs text-[#8a8f99] line-clamp-1">{t('dashboard.venueStatisticsDesc')}</p>
            </Link>
            <Link
              to="/reports"
              className="p-3 card-dark-hover text-left group"
            >
              <h3 className="font-semibold text-white mb-1 group-hover:text-[#00AFF2] transition-colors text-sm">{t('dashboard.revenueReports')}</h3>
              <p className="text-xs text-[#8a8f99] line-clamp-1">{t('dashboard.revenueReportsDesc')}</p>
            </Link>
            <Link
              to="/sessions"
              className="p-3 card-dark-hover text-left group"
            >
              <h3 className="font-semibold text-white mb-1 group-hover:text-[#00AFF2] transition-colors text-sm">{t('dashboard.sessionManagement')}</h3>
              <p className="text-xs text-[#8a8f99] line-clamp-1">{t('dashboard.sessionManagementDesc')}</p>
            </Link>
          </div>
        </div>
      )}

      {/* Quick Actions - For Regular Users */}
      {!isAdmin && !isVenueOperator && (
        <div className="mb-4">
          <h2 className="text-xl font-bold text-white mb-3">🎯 Gyors Műveletek</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <button className="card-dark p-4 text-left group hover:border-[#00AFF2] border-2 border-transparent transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00AFF2] to-[#0089be] rounded-full flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white mb-1 group-hover:text-[#00AFF2] transition-colors">Új Játék Indítása</h3>
              <p className="text-[#B8B8C3] text-xs">Kezdj el egy új darts mérkőzést</p>
            </button>

            <Link
              to="/profile"
              className="card-dark p-3 text-left group hover:border-[#2bc48a] border-2 border-transparent transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-[#2bc48a] to-[#00d9a8] rounded-full flex items-center justify-center mb-1 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white mb-1 group-hover:text-[#2bc48a] transition-colors">Profilom</h3>
              <p className="text-[#B8B8C3] text-xs">Nézd meg statisztikáidat és eredményeidet</p>
            </Link>

            <Link
              to="/analytics"
              className="card-dark p-3 text-left group hover:border-[#d9a514] border-2 border-transparent transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-[#d9a514] to-[#ffb800] rounded-full flex items-center justify-center mb-1 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white mb-1 group-hover:text-[#d9a514] transition-colors">Statisztikák</h3>
              <p className="text-[#B8B8C3] text-xs">Tekintsd meg a részletes elemzéseket</p>
            </Link>
          </div>
        </div>
      )}

      {/* Top Players Leaderboard - For Regular Users */}
      {!isAdmin && !isVenueOperator && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold text-white">🏆 Top Játékosok</h2>
            <Link
              to="/leaderboard"
              className="text-[#00AFF2] hover:text-[#2bc48a] text-sm font-medium transition-colors"
            >
              Teljes Lista →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {topPlayers && topPlayers.length > 0 ? topPlayers.map((player) => {
              const badge = player.rank === 1 ? '🥇' : player.rank === 2 ? '🥈' : '🥉';
              const color = player.rank === 1 ? 'from-yellow-400 to-yellow-600' :
                player.rank === 2 ? 'from-gray-300 to-gray-500' :
                  'from-orange-400 to-orange-600';

              return (
                <div key={player.userId} className="card-dark p-4 relative overflow-hidden group hover:border-[#00AFF2] border-2 border-transparent transition-all">
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${color}`}></div>
                  <div className="flex items-center gap-3">
                    <div className="text-4xl">{badge}</div>
                    <div className="flex-1">
                      <p className="text-lg font-bold text-white group-hover:text-[#00AFF2] transition-colors">{player.name}</p>
                      <p className="text-[#B8B8C3] text-sm">#{player.rank} helyezett</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#2bc48a]">{player.score}</p>
                      <p className="text-xs text-[#B8B8C3]">pontszám</p>
                    </div>
                  </div>
                </div>
              );
            }) : (
              <div className="col-span-3 text-center py-8">
                <p className="text-[#B8B8C3]">Betöltés...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Game Modes Info - For Regular Users */}
      {!isAdmin && !isVenueOperator && (
        <div className="mb-4">
          <h2 className="text-xl font-bold text-white mb-3">🎮 Játékmódok</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="card-dark p-4 border-l-4 border-[#00AFF2]">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 bg-[#00AFF2]/10 rounded-lg flex items-center justify-center">
                  <span className="text-xl font-bold text-[#00AFF2]">501</span>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">501 Játék</h3>
                  <p className="text-xs text-[#B8B8C3]">Klasszikus mód</p>
                </div>
              </div>
              <p className="text-xs text-[#B8B8C3] mb-2">Indulj 501 pontról és csökkentsd 0-ra. Dupla dobással kell befejezni!</p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#2bc48a]">⚡ Legnépszerűbb</span>
                <span className="text-[#B8B8C3]">~15-20 perc</span>
              </div>
            </div>

            <div className="card-dark p-4 border-l-4 border-[#2bc48a]">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 bg-[#2bc48a]/10 rounded-lg flex items-center justify-center">
                  <span className="text-xl font-bold text-[#2bc48a]">301</span>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">301 Játék</h3>
                  <p className="text-xs text-[#B8B8C3]">Gyors mód</p>
                </div>
              </div>
              <p className="text-xs text-[#B8B8C3] mb-2">Rövidebb változat 301 ponttal. Tökéletes gyors mérkőzésekhez!</p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-green-400">⚡ Gyors</span>
                <span className="text-[#B8B8C3]">~10-15 perc</span>
              </div>
            </div>

            <div className="card-dark p-4 border-l-4 border-[#d9a514]">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 bg-[#d9a514]/10 rounded-lg flex items-center justify-center">
                  <span className="text-xl font-bold text-[#d9a514]">🦗</span>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">Cricket</h3>
                  <p className="text-xs text-[#B8B8C3]">Stratégiai mód</p>
                </div>
              </div>
              <p className="text-xs text-[#B8B8C3] mb-2">Zárd le a számokat (15-20 és bull) és gyűjts pontokat!</p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#d9a514]">🧠 Taktikai</span>
                <span className="text-[#B8B8C3]">~20-25 perc</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Achievements Preview - For Regular Users */}
      {!isAdmin && !isVenueOperator && (
        <div className="card-dark p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold text-white">🎖️ Legutóbbi Eredmények</h2>
            <Link
              to="/profile"
              className="text-[#00AFF2] hover:text-[#2bc48a] text-sm font-medium transition-colors"
            >
              Összes Megtekintése →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              { icon: '🏆', title: 'Első Győzelem', unlocked: true, date: '2024-01-15' },
              { icon: '💯', title: 'Century Club', unlocked: true, date: '2024-02-03' },
              { icon: '🎯', title: 'Maximum 180', unlocked: true, date: '2024-01-28' },
              { icon: '⭐', title: 'Veterán', unlocked: false }
            ].map((achievement, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg text-center transition-all ${achievement.unlocked
                  ? 'bg-gradient-to-br from-[#00AFF2]/10 to-[#2bc48a]/10 border-2 border-[#00AFF2]/30'
                  : 'bg-[#15191E] opacity-50'
                  }`}
              >
                <div className="text-3xl mb-1">{achievement.icon}</div>
                <p className="text-xs font-semibold text-white mb-1">{achievement.title}</p>
                {achievement.unlocked ? (
                  <p className="text-xs text-[#2bc48a]">✓ Feloldva</p>
                ) : (
                  <p className="text-xs text-[#8a8f99]">🔒 Zárolva</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity - Only for Admins and Venue Operators */}
      {(isAdmin || isVenueOperator) && (
        <div className="card-dark p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold text-gradient-cyan">{t('dashboard.recentActivity')}</h2>
            <button
              onClick={() => setShowAllActivitiesModal(true)}
              className="btn-primary text-sm"
            >
              {t('dashboard.allActivities')}
            </button>
          </div>
          <div className="space-y-3">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => (
                <div key={activity.id} className="leaderboard-card flex items-center gap-4 p-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activity.type === 'session' ? 'bg-gradient-to-br from-[#00AFF2] to-[#0089be]' :
                    activity.type === 'venue' ? 'bg-gradient-to-br from-[#2bc48a] to-[#00d9a8]' :
                      'bg-gradient-to-br from-[#d9a514] to-[#ffb800]'
                    }`}>
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {activity.type === 'session' && (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      )}
                      {activity.type === 'venue' && (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      )}
                      {activity.type === 'user' && (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      )}
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-white truncate">{activity.description}</p>
                      {activity.result && activity.type === 'session' && (
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${activity.result === 'win' ? 'bg-green-500/20 text-green-400' :
                          activity.result === 'loss' ? 'bg-red-500/20 text-red-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                          {activity.result === 'win' && t('common.won')}
                          {activity.result === 'loss' && t('common.lost')}
                          {activity.result === 'draw' && t('common.draw')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#8a8f99]">{activity.timestamp}</p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedActivity(activity);
                      setShowActivityModal(true);
                    }}
                    className="text-[#00AFF2] hover:text-[#2bc48a] text-sm font-medium transition-colors flex-shrink-0"
                  >
                    {t('dashboard.view')}
                  </button>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-[#8a8f99]">{t('dashboard.noRecentActivity')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Activity Detail Modal */}
      {showActivityModal && selectedActivity && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" onClick={() => setShowActivityModal(false)}>
          <div className="card-dark p-8 w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gradient-cyan">{t('dashboard.activityDetails')}</h3>
              <button
                onClick={() => setShowActivityModal(false)}
                className="text-[#8a8f99] hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-[#1d232a] rounded-lg">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${selectedActivity.type === 'session' ? 'bg-gradient-to-br from-[#00AFF2] to-[#0089be]' :
                  selectedActivity.type === 'venue' ? 'bg-gradient-to-br from-[#2bc48a] to-[#00d9a8]' :
                    'bg-gradient-to-br from-[#d9a514] to-[#ffb800]'
                  }`}>
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {selectedActivity.type === 'session' && (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    )}
                    {selectedActivity.type === 'venue' && (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    )}
                    {selectedActivity.type === 'user' && (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    )}
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-lg font-semibold text-white">{selectedActivity.description}</p>
                  <p className="text-sm text-[#8a8f99]">{selectedActivity.timestamp}</p>
                </div>
              </div>

              <div className="bg-[#1d232a] rounded-lg p-6 space-y-4">
                {selectedActivity.createdByName && (
                  <div>
                    <p className="text-sm text-[#8a8f99] mb-1">{t('dashboard.createdBy')}</p>
                    <p className="text-white font-medium">{selectedActivity.createdByName}</p>
                  </div>
                )}

                {selectedActivity.venueName && (
                  <div>
                    <p className="text-sm text-[#8a8f99] mb-1">{t('common.venue')}</p>
                    <p className="text-white font-medium">{selectedActivity.venueName}</p>
                  </div>
                )}

                <div>
                  <p className="text-sm text-[#8a8f99] mb-1">{t('dashboard.exactDate')}</p>
                  <p className="text-white font-medium">{selectedActivity.exactTimestamp || selectedActivity.timestamp}</p>
                </div>

                <div>
                  <p className="text-sm text-[#8a8f99] mb-1">{t('dashboard.description')}</p>
                  <p className="text-white">{selectedActivity.description}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowActivityModal(false)}
                className="btn-secondary"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* All Activities Modal */}
      {showAllActivitiesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" onClick={() => setShowAllActivitiesModal(false)}>
          <div className="card-dark p-8 w-full max-w-4xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gradient-cyan">{t('dashboard.allActivities')}</h3>
              <button
                onClick={() => setShowAllActivitiesModal(false)}
                className="text-[#8a8f99] hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-3">
              {allActivities.length > 0 ? (
                allActivities.map((activity) => (
                  <div key={activity.id} className="leaderboard-card flex items-center gap-4 p-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${activity.type === 'session' ? 'bg-gradient-to-br from-[#00AFF2] to-[#0089be]' :
                      activity.type === 'venue' ? 'bg-gradient-to-br from-[#2bc48a] to-[#00d9a8]' :
                        'bg-gradient-to-br from-[#d9a514] to-[#ffb800]'
                      }`}>
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {activity.type === 'session' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        )}
                        {activity.type === 'venue' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        )}
                        {activity.type === 'user' && (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        )}
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-white truncate">{activity.description}</p>
                        {activity.result && activity.type === 'session' && (
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${activity.result === 'win' ? 'bg-green-500/20 text-green-400' :
                            activity.result === 'loss' ? 'bg-red-500/20 text-red-400' :
                              'bg-yellow-500/20 text-yellow-400'
                            }`}>
                            {activity.result === 'win' && t('common.won')}
                            {activity.result === 'loss' && t('common.lost')}
                            {activity.result === 'draw' && t('common.draw')}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#8a8f99]">{activity.timestamp}</p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedActivity(activity);
                        setShowAllActivitiesModal(false);
                        setShowActivityModal(true);
                      }}
                      className="text-[#00AFF2] hover:text-[#2bc48a] text-sm font-medium transition-colors flex-shrink-0"
                    >
                      {t('dashboard.view')}
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-[#8a8f99]">{t('dashboard.noRecentActivity')}</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowAllActivitiesModal(false)}
                className="btn-secondary"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
